import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Resizable } from 'react-resizable';
import { researchService } from '../../services/researchService';

// ---------- chart themes ----------
const CHART_THEMES = {
  hff: {
    label: 'HFF',
    series: [
      '#0b3a6e', '#7a1f2b', '#1e5aa8', '#b33a3a', '#4a5563',
      '#0b3a6e', '#7a1f2b', '#1e5aa8', '#b33a3a', '#4a5563',
    ],
    cardBg: '#ffffff',
    cardBorder: '#dee2e6',
    textMuted: '#6c757d',
    gridStroke: '#e9ecef',
    axisStroke: '#adb5bd',
    tooltipBg: '#ffffff',
    tooltipBorder: '#dee2e6',
    tooltipText: '#212529',
    resizeGrip: '#adb5bd',
    zeroLine: '#888',
    chartBg: '#ffffff',
    pageBg: '#f8f9fa',
    pageText: '#212529',
    panelBg: '#ffffff',
    panelBorder: '#dee2e6',
    subtleBg: '#f8f9fa',
    inputBg: '#ffffff',
    inputBorder: '#ced4da',
    inputText: '#212529',
    inputPlaceholder: '#6c757d',
    stripeStroke: '#ffffff',
    stripeOpacity: 0.55,
  },
  rebellionaire: {
    label: 'Rebellionaire',
    series: [
      '#c9a84c', '#dd9944', '#adb5bd', '#6c757d', '#88ccaa',
      '#c9a84c', '#dd9944', '#adb5bd', '#6c757d', '#88ccaa',
    ],
    cardBg: '#000000',
    cardBorder: '#3a2e00',
    textMuted: '#9a8a60',
    gridStroke: '#2f2a1a',
    axisStroke: '#7a6a40',
    tooltipBg: '#17140b',
    tooltipBorder: '#3a2e00',
    tooltipText: '#f3e6b5',
    resizeGrip: '#7a6a40',
    zeroLine: '#9a8a60',
    chartBg: '#000000',
    pageBg: '#000000',
    pageText: '#f3e6b5',
    panelBg: '#000000',
    panelBorder: '#3a2e00',
    subtleBg: '#000000',
    inputBg: '#1b150a',
    inputBorder: '#4b3b10',
    inputText: '#f3e6b5',
    inputPlaceholder: '#9a8a60',
    stripeStroke: '#000000',
    stripeOpacity: 0.45,
  },
};

function renderStripeDefs(patternPrefix, chartTheme) {
  return (
    <defs>
      {[0, 1, 2, 3, 4].map((i) => (
        <pattern
          key={`${patternPrefix}-stripe-${i}`}
          id={`${patternPrefix}-stripe-${i}`}
          patternUnits="userSpaceOnUse"
          width="8"
          height="8"
          patternTransform="rotate(45)"
        >
          <rect width="8" height="8" fill={chartTheme.series[i]} />
          <line x1="0" y1="0" x2="0" y2="8" stroke={chartTheme.stripeStroke} strokeOpacity={chartTheme.stripeOpacity} strokeWidth="3" />
        </pattern>
      ))}
    </defs>
  );
}

// ---------- card resize constraints ----------
// Charts live in resizable *cards* (the whole card resizes, not just the chart):
// dragging a handle grows the card and the chart fills it. Predefined cards
// flex-wrap, so growing one reflows the rest; the custom card fills its container
// width by default (see ResizableCard `fillWidth`).
const MIN_CARD_WIDTH = 280;         // card never narrower than this
const MAX_CARD_WIDTH = 4000;        // card width ceiling
const MIN_CARD_HEIGHT = 200;        // card never shorter than this
const MAX_CARD_HEIGHT = 2000;       // card height ceiling
const DEFAULT_CARD_WIDTH = 460;     // ~two cards per row on a wide screen
const DEFAULT_CARD_HEIGHT = 300;    // initial card height (header + chart body)
const PREDEFINED_GRID_GAP = 16;     // matches flex gap-3 (1rem)

// Below these the chart body scrolls instead of squishing into illegibility.
const MIN_CHART_HEIGHT = 140;       // readable chart-body minimum height
const MIN_CHART_WIDTH = 240;        // readable chart-body minimum width
const RESIZE_SYNC_EPSILON_PX = 2;   // ignore sub-pixel/zoom jitter
const CHART_RESIZE_DEBOUNCE_MS = 120;

const EXPORT_ASPECT_OPTIONS = [
  { value: '1:1', label: '1:1', w: 1, h: 1 },
  { value: '4:3', label: '4:3', w: 4, h: 3 },
  { value: '6:4', label: '6:4', w: 6, h: 4 },
  { value: '7:5', label: '7:5', w: 7, h: 5 },
  { value: '16:9', label: '16:9', w: 16, h: 9 },
];

const EXPORT_SIZE_PRESETS = {
  small: 240,
  medium: 480,
  large: 960,
  'x-large': 1440,
};

const MIN_CUSTOM_EXPORT_DIMENSION = 16;
const MAX_CUSTOM_EXPORT_DIMENSION = 8000;
const EXPORT_TITLE_HEIGHT = 32;
const EXPORT_LEGEND_HEIGHT = 32;

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

function waitForAnimationFrames(frameCount = 2) {
  return new Promise((resolve) => {
    const tick = (remaining) => {
      if (remaining <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(() => tick(remaining - 1));
    };
    tick(frameCount);
  });
}

function getPrimaryChartSvg(surfaceEl) {
  const explicitSurface = surfaceEl?.querySelector('svg.recharts-surface');
  if (explicitSurface) return explicitSurface;

  const svgs = Array.from(surfaceEl?.querySelectorAll('svg') || []);
  if (!svgs.length) return null;

  let best = svgs[0];
  let bestArea = 0;
  svgs.forEach((svg) => {
    const rect = svg.getBoundingClientRect();
    const area = Math.max(0, rect.width) * Math.max(0, rect.height);
    if (area > bestArea) {
      bestArea = area;
      best = svg;
    }
  });
  return best;
}

async function waitForSvgResize(surfaceEl, targetWidth, targetHeight, timeoutMs = 1400) {
  const start = performance.now();
  let lastSvg = null;

  return new Promise((resolve) => {
    const tick = () => {
      const svg = getPrimaryChartSvg(surfaceEl);
      if (svg) {
        lastSvg = svg;
        const rect = svg.getBoundingClientRect();
        const widthOk = Math.abs(rect.width - targetWidth) <= 2;
        const heightOk = Math.abs(rect.height - targetHeight) <= 2;
        if (widthOk && heightOk) {
          resolve(svg);
          return;
        }
      }

      if (performance.now() - start > timeoutMs) {
        resolve(lastSvg);
        return;
      }
      requestAnimationFrame(tick);
    };

    tick();
  });
}

function sanitizeFilenamePart(value) {
  return String(value || 'chart')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'chart';
}

// Sanitizes a full user-typed filename: keeps casing/spacing, only strips
// characters that are invalid on common filesystems.
function sanitizeFullFilename(value) {
  const trimmed = String(value || '').trim().replace(/[\\/:*?"<>|]+/g, '-');
  return trimmed || 'chart';
}

async function exportSvgAsPng({
  svgEl,
  canvasWidth,
  canvasHeight,
  chartAreaY,
  chartAreaHeight,
  filename,
  backgroundColor,
  title,
  legendItems = [],
  textColor = '#212529',
}) {
  if (!svgEl) throw new Error('Chart SVG not found');
  const rect = svgEl.getBoundingClientRect();
  const sourceWidth = Math.max(1, Math.round(rect.width || svgEl.clientWidth || Number(svgEl.getAttribute('width')) || canvasWidth));
  const sourceHeight = Math.max(1, Math.round(rect.height || svgEl.clientHeight || Number(svgEl.getAttribute('height')) || chartAreaHeight));

  const cloned = svgEl.cloneNode(true);
  cloned.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  cloned.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  if (!cloned.getAttribute('viewBox')) {
    cloned.setAttribute('viewBox', `0 0 ${sourceWidth} ${sourceHeight}`);
  }
  cloned.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  cloned.setAttribute('width', String(sourceWidth));
  cloned.setAttribute('height', String(sourceHeight));

  const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bgRect.setAttribute('x', '0');
  bgRect.setAttribute('y', '0');
  bgRect.setAttribute('width', '100%');
  bgRect.setAttribute('height', '100%');
  bgRect.setAttribute('fill', backgroundColor || '#ffffff');
  cloned.insertBefore(bgRect, cloned.firstChild);

  const serializer = new XMLSerializer();
  const svgMarkup = serializer.serializeToString(cloned);
  const svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to load chart image'));
      image.src = svgUrl;
    });

    const fullWidth = Math.max(1, Math.round(canvasWidth || sourceWidth));
    const fullHeight = Math.max(1, Math.round(canvasHeight || sourceHeight));
    const drawY = Math.max(0, Math.round(chartAreaY || 0));

    const canvas = document.createElement('canvas');
    canvas.width = fullWidth;
    canvas.height = fullHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context unavailable');
    ctx.fillStyle = backgroundColor || '#ffffff';
    ctx.fillRect(0, 0, fullWidth, fullHeight);

    const hasTitle = Boolean((title || '').trim());
    const hasLegend = Array.isArray(legendItems) && legendItems.length > 0;

    if (hasTitle) {
      ctx.fillStyle = textColor;
      ctx.font = '600 18px Segoe UI, Arial, sans-serif';
      ctx.textBaseline = 'top';
      ctx.fillText(title.trim(), 12, 8);
    }

    // Draw the chart at its own native rendered size (no stretching/squishing) —
    // the export frame already reserved title/legend space before render.
    ctx.drawImage(img, 0, drawY, sourceWidth, sourceHeight);

    if (hasLegend) {
      const box = 10;
      const gap = 8;
      const itemGap = 16;
      ctx.font = '12px Segoe UI, Arial, sans-serif';
      ctx.textBaseline = 'middle';

      const measured = legendItems.map((item) => {
        const label = String(item?.label || '');
        return {
          label,
          color: item?.color || '#6c757d',
          width: box + gap + ctx.measureText(label).width,
        };
      });

      const totalWidth = measured.reduce((sum, m, idx) => sum + m.width + (idx > 0 ? itemGap : 0), 0);
      let x = Math.max(12, (fullWidth - totalWidth) / 2);
      const y = fullHeight - 15;
      measured.forEach((item, idx) => {
        if (idx > 0) x += itemGap;
        ctx.fillStyle = item.color;
        ctx.fillRect(x, y - box / 2, box, box);
        ctx.fillStyle = textColor;
        ctx.fillText(item.label, x + box + gap, y);
        x += item.width;
      });
    }

    await new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to encode PNG'));
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        resolve();
      }, 'image/png');
    });
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

// Custom drag handles styled to match the light theme. Positioning and cursor
// depend on which edge/corner the handle represents.
function renderResizeHandle(handleAxis, ref, chartTheme) {
  const base = {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    touchAction: 'none',
    zIndex: 2,
  };
  const grip = { borderRadius: 2, backgroundColor: chartTheme.resizeGrip };

  if (handleAxis === 'e') {
    return (
      <div ref={ref} className="react-resizable-handle react-resizable-handle-e" title="Drag to resize"
        style={{ ...base, top: '50%', right: 0, transform: 'translateY(-50%)', width: 12, height: 64, cursor: 'ew-resize' }}>
        <div style={{ ...grip, width: 3, height: 40 }} />
      </div>
    );
  }
  if (handleAxis === 'se') {
    return (
      <div ref={ref} className="react-resizable-handle react-resizable-handle-se" title="Drag to resize"
        style={{ ...base, bottom: 0, right: 0, width: 16, height: 16, cursor: 'nwse-resize' }}>
        <div style={{ width: 8, height: 8, borderRight: `2px solid ${chartTheme.resizeGrip}`, borderBottom: `2px solid ${chartTheme.resizeGrip}` }} />
      </div>
    );
  }
  // South (height) handle — default.
  return (
    <div ref={ref} className="react-resizable-handle react-resizable-handle-s" title="Drag to resize"
      style={{ ...base, bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 64, height: 12, cursor: 'ns-resize' }}>
      <div style={{ ...grip, width: 40, height: 3 }} />
    </div>
  );
}

/**
 * ResizableCard
 *
 * A self-contained, resizable chart card. The *whole card* (border, title, chart)
 * is the resize target: dragging a handle grows the card and the chart fills it
 * via ResponsiveContainer.
 *
 * Layout modes:
 *   • Default (predefined charts) — fixed default width; cards flex-wrap, so
 *     growing one pushes the others to reflow onto the next row.
 *   • `fillWidth` (custom chart)  — width follows the container responsively
 *     until the user drags the east/corner handle, after which it locks.
 *
 * Per-card controls:
 *   • Hide       — collapse the card to a restore chip (handled by the parent).
 *   • Fullscreen — render the card as a fixed full-viewport overlay (Esc exits).
 *
 * The chart body scrolls (rather than squishing) once a dimension drops below a
 * readable minimum.
 */
function ResizableCard({
  title,
  fullscreen,
  onHide,
  onToggleFullscreen,
  chartTheme,
  companyName,
  exportLegendItems = [],
  renderChart,
  children,
  fillWidth = false,
  defaultWidth = DEFAULT_CARD_WIDTH,
  defaultHeight = DEFAULT_CARD_HEIGHT,
}) {
  const areaRef = useRef(null);
  const exportRenderRef = useRef(null);
  const cardSurfaceRef = useRef(null);
  const cardId = useId().replace(/:/g, '');
  const userResizedWidthRef = useRef(false);
  const [width, setWidth] = useState(clamp(Number(defaultWidth) || DEFAULT_CARD_WIDTH, MIN_CARD_WIDTH, MAX_CARD_WIDTH));
  const [height, setHeight] = useState(defaultHeight);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportAspect, setExportAspect] = useState('16:9');
  const [exportSize, setExportSize] = useState('medium');
  const [useCustomExportSize, setUseCustomExportSize] = useState(false);
  const [customExportWidth, setCustomExportWidth] = useState(1600);
  const [customExportHeight, setCustomExportHeight] = useState(900);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportShowTitle, setExportShowTitle] = useState(true);
  const [exportTitleText, setExportTitleText] = useState('');
  const [exportShowLegend, setExportShowLegend] = useState(true);
  const [exportLegendLabels, setExportLegendLabels] = useState({});
  const [exportItemHidden, setExportItemHidden] = useState({});
  const [exportItemColor, setExportItemColor] = useState({});
  const [customFileName, setCustomFileName] = useState('');

  const livePatternPrefix = useMemo(() => `card-${cardId}`, [cardId]);
  const previewPatternPrefix = useMemo(() => `preview-${cardId}`, [cardId]);
  const exportPatternPrefix = useMemo(() => `export-${cardId}`, [cardId]);

  // Keep width aligned to updated defaults until the user manually resizes width.
  useEffect(() => {
    if (userResizedWidthRef.current) return;
    const next = Number(defaultWidth);
    if (!Number.isFinite(next) || next <= 0) return;
    setWidth(clamp(next, MIN_CARD_WIDTH, MAX_CARD_WIDTH));
  }, [defaultWidth]);

  // In `fillWidth` mode, width tracks the container until the user drags east.
  useLayoutEffect(() => {
    if (!fillWidth) return undefined;
    const el = areaRef.current;
    if (!el) return undefined;
    let rafId = 0;
    const sync = () => {
      const measuredWidth = Math.floor(el.getBoundingClientRect().width || 0);
      if (!userResizedWidthRef.current && measuredWidth > 0) {
        const next = clamp(measuredWidth, MIN_CARD_WIDTH, MAX_CARD_WIDTH);
        setWidth((prev) => (Math.abs(prev - next) < RESIZE_SYNC_EPSILON_PX ? prev : next));
      }
    };
    const scheduleSync = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(sync);
    };
    sync();
    const ro = new ResizeObserver(scheduleSync);
    ro.observe(el);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [fillWidth]);

  const handleResize = useCallback((_e, { size, handle }) => {
    // `handle` is the axis string ('s', 'e', 'se', …); lock only the dragged axes.
    if (handle.includes('e')) {
      userResizedWidthRef.current = true;
      setWidth(clamp(size.width, MIN_CARD_WIDTH, MAX_CARD_WIDTH));
    }
    if (handle.includes('s')) {
      setHeight(clamp(size.height, MIN_CARD_HEIGHT, MAX_CARD_HEIGHT));
    }
  }, []);

  // Esc exits fullscreen.
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onToggleFullscreen(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen, onToggleFullscreen]);

  const selectedAspect = useMemo(
    () => EXPORT_ASPECT_OPTIONS.find((a) => a.value === exportAspect) || EXPORT_ASPECT_OPTIONS[4],
    [exportAspect],
  );

  const computedExportDimensions = useMemo(() => {
    if (useCustomExportSize) {
      const w = clamp(Number(customExportWidth) || 1600, MIN_CUSTOM_EXPORT_DIMENSION, MAX_CUSTOM_EXPORT_DIMENSION);
      const h = clamp(Number(customExportHeight) || 900, MIN_CUSTOM_EXPORT_DIMENSION, MAX_CUSTOM_EXPORT_DIMENSION);
      return { width: Math.round(w), height: Math.round(h) };
    }
    const baseWidth = EXPORT_SIZE_PRESETS[exportSize] || EXPORT_SIZE_PRESETS.medium;
    const ratio = selectedAspect.h / selectedAspect.w;
    return { width: baseWidth, height: Math.round(baseWidth * ratio) };
  }, [useCustomExportSize, exportSize, customExportWidth, customExportHeight, selectedAspect]);

  // Buffer accounts for nested padding/borders between the modal edge and the
  // preview frame (content px-3 + preview panel padding/border + scroll box
  // border) so the preview never needs a horizontal scrollbar at its own size.
  const modalWidthPx = useMemo(
    () => clamp(computedExportDimensions.width + 96, 560, 2400),
    [computedExportDimensions.width],
  );

  // Effective title/legend after applying the user's show/hide/rename overrides.
  const effectiveTitleText = (exportTitleText.trim() || title || '').trim();
  const hasExportTitle = exportShowTitle && Boolean(effectiveTitleText);

  const effectiveLegendItems = useMemo(() => {
    if (!exportShowLegend) return [];
    return exportLegendItems
      .map((item, idx) => ({
        ...item,
        label: (exportLegendLabels[idx] || '').trim() || item.label,
        color: exportItemColor[idx] || item.color,
        hidden: !!exportItemHidden[idx],
      }))
      .filter((item) => !item.hidden);
  }, [exportShowLegend, exportLegendItems, exportLegendLabels, exportItemColor, exportItemHidden]);
  const hasExportLegend = effectiveLegendItems.length > 0;

  // Per-series hidden/color overrides threaded into renderChart for preview & export
  // only (the live editable chart on the page is unaffected).
  const seriesOverrides = useMemo(
    () => exportLegendItems.map((_, idx) => ({
      hidden: !!exportItemHidden[idx],
      color: exportItemColor[idx] || null,
    })),
    [exportLegendItems, exportItemHidden, exportItemColor],
  );

  const exportTitleAreaHeight = hasExportTitle ? EXPORT_TITLE_HEIGHT : 0;
  const exportLegendAreaHeight = hasExportLegend ? EXPORT_LEGEND_HEIGHT : 0;
  const exportChartAreaHeight = Math.max(
    20,
    computedExportDimensions.height - exportTitleAreaHeight - exportLegendAreaHeight,
  );

  // Default filename: <company>_<title>_<size>.png — user can override entirely.
  const defaultFileNameBase = useMemo(() => {
    const sizePart = useCustomExportSize
      ? `${computedExportDimensions.width}x${computedExportDimensions.height}`
      : `${exportAspect}-${exportSize}`;
    return [companyName, effectiveTitleText || title, sizePart]
      .map((p) => sanitizeFilenamePart(p))
      .filter(Boolean)
      .join('_') || 'chart';
  }, [companyName, effectiveTitleText, title, useCustomExportSize, computedExportDimensions, exportAspect, exportSize]);

  const handleExport = useCallback(async () => {
    const surfaceEl = exportRenderRef.current;
    if (!surfaceEl) {
      setExportError('Export preview is not ready yet.');
      return;
    }

    try {
      setExporting(true);
      setExportError('');

      await waitForAnimationFrames(2);
      const svgEl = await waitForSvgResize(
        surfaceEl,
        computedExportDimensions.width,
        exportChartAreaHeight,
      );
      if (!svgEl) {
        throw new Error('Chart SVG did not render at export size.');
      }
      const rawName = sanitizeFullFilename(customFileName.trim() || defaultFileNameBase);
      const filename = /\.png$/i.test(rawName) ? rawName : `${rawName}.png`;
      await exportSvgAsPng({
        svgEl,
        canvasWidth: computedExportDimensions.width,
        canvasHeight: computedExportDimensions.height,
        chartAreaY: exportTitleAreaHeight,
        chartAreaHeight: exportChartAreaHeight,
        filename,
        backgroundColor: chartTheme.chartBg,
        title: hasExportTitle ? effectiveTitleText : '',
        legendItems: effectiveLegendItems,
        textColor: chartTheme.pageText,
      });
      setShowExportModal(false);
    } catch (err) {
      setExportError(err?.message || 'Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [
    customFileName,
    defaultFileNameBase,
    computedExportDimensions,
    exportTitleAreaHeight,
    exportChartAreaHeight,
    chartTheme.chartBg,
    chartTheme.pageText,
    hasExportTitle,
    effectiveTitleText,
    effectiveLegendItems,
  ]);

  // Shared layout for the modal preview and the hidden capture target — both
  // render identically (title bar + chart + legend bar) so what the user sees
  // in the preview is exactly what gets exported.
  const renderExportFrame = useCallback((patternPrefix, frameRef) => (
    <div
      ref={frameRef}
      style={{
        width: computedExportDimensions.width,
        height: computedExportDimensions.height,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: chartTheme.chartBg,
      }}
    >
      {hasExportTitle && (
        <div
          style={{
            height: exportTitleAreaHeight,
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 12,
            fontWeight: 600,
            fontSize: 16,
            color: chartTheme.pageText,
          }}
        >
          {effectiveTitleText}
        </div>
      )}
      <div style={{ height: exportChartAreaHeight, flex: '0 0 auto', width: '100%', minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%" debounce={CHART_RESIZE_DEBOUNCE_MS}>
          {typeof renderChart === 'function' ? renderChart(patternPrefix, { showLegend: false, seriesOverrides }) : children}
        </ResponsiveContainer>
      </div>
      {hasExportLegend && (
        <div
          style={{
            height: exportLegendAreaHeight,
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 16,
            fontSize: 12,
            color: chartTheme.pageText,
          }}
        >
          {effectiveLegendItems.map((item, idx) => (
            <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: item.color, display: 'inline-block' }} />
              {item.label}
            </span>
          ))}
        </div>
      )}
    </div>
  ), [
    computedExportDimensions,
    chartTheme.chartBg,
    chartTheme.pageText,
    hasExportTitle,
    hasExportLegend,
    exportTitleAreaHeight,
    exportChartAreaHeight,
    exportLegendAreaHeight,
    effectiveTitleText,
    effectiveLegendItems,
    seriesOverrides,
    renderChart,
    children,
  ]);

  const header = (
    <div className="d-flex align-items-center mb-2">
      <span className="fw-semibold small">{title}</span>
      <div className="ms-auto d-flex gap-1">
        <button className="btn btn-sm btn-outline-secondary py-0 px-1" style={{ fontSize: '0.7rem' }}
          title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          onClick={onToggleFullscreen}>
          <i className={`bi ${fullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`} />
        </button>
        <button
          className="btn btn-sm btn-outline-secondary py-0 px-1"
          style={{ fontSize: '0.7rem' }}
          title="Export PNG"
          aria-label="Export PNG"
          onClick={() => {
            setExportError('');
            setShowExportModal(true);
          }}
        >
          <i className="bi bi-download" />
        </button>
        {!fullscreen && (
          <button className="btn btn-sm btn-outline-secondary py-0 px-1" style={{ fontSize: '0.7rem' }}
            title="Hide chart" aria-label="Hide chart" onClick={onHide}>
            <i className="bi bi-eye-slash" />
          </button>
        )}
      </div>
    </div>
  );

  // Chart body: fills the card; scrolls once below a readable minimum.
  const body = (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', scrollbarGutter: 'stable both-edges' }}>
      <div ref={cardSurfaceRef} style={{ width: '100%', height: '100%', minWidth: MIN_CHART_WIDTH, minHeight: MIN_CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%" debounce={CHART_RESIZE_DEBOUNCE_MS}>
          {typeof renderChart === 'function' ? renderChart(livePatternPrefix) : children}
        </ResponsiveContainer>
      </div>
    </div>
  );

  const exportModal = showExportModal && (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1080 }}
      onClick={() => !exporting && setShowExportModal(false)}
    >
      <div
        className="border rounded shadow d-flex flex-column"
        style={{ width: `min(${modalWidthPx}px, 96vw)`, maxHeight: '90vh', backgroundColor: '#fff', color: '#212529' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`export-modal-title-${cardId}`}
        tabIndex={-1}
      >
        <div className="d-flex align-items-center p-3 pb-0 mb-3" style={{ flex: '0 0 auto' }}>
          <h6 className="mb-0" id={`export-modal-title-${cardId}`}>Export Chart as PNG</h6>
          <button
            className="btn btn-sm btn-outline-secondary ms-auto"
            onClick={() => !exporting && setShowExportModal(false)}
            disabled={exporting}
          >
            <i className="bi bi-x" />
          </button>
        </div>

        <div className="px-3" style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        <div className="row g-2 mb-3">
          <div className="col-6">
            <label className="form-label small fw-semibold mb-1">Aspect Ratio (Landscape)</label>
            <select
              className="form-select form-select-sm"
              value={exportAspect}
              onChange={(e) => setExportAspect(e.target.value)}
              disabled={exporting || useCustomExportSize}
            >
              {EXPORT_ASPECT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="col-6">
            <label className="form-label small fw-semibold mb-1">Size</label>
            <select
              className="form-select form-select-sm"
              value={exportSize}
              onChange={(e) => setExportSize(e.target.value)}
              disabled={exporting || useCustomExportSize}
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
              <option value="x-large">X-Large</option>
            </select>
          </div>
        </div>

        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id={`custom-export-size-${cardId}`}
            checked={useCustomExportSize}
            onChange={(e) => setUseCustomExportSize(e.target.checked)}
            disabled={exporting}
          />
          <label className="form-check-label small" htmlFor={`custom-export-size-${cardId}`}>
            Use custom dimensions (overrides size and aspect ratio)
          </label>
        </div>

        {useCustomExportSize && (
          <div className="row g-2 mb-3">
            <div className="col-6">
              <label className="form-label small fw-semibold mb-1">Width (px)</label>
              <input
                type="number"
                className="form-control form-control-sm"
                min={MIN_CUSTOM_EXPORT_DIMENSION}
                max={MAX_CUSTOM_EXPORT_DIMENSION}
                value={customExportWidth}
                onChange={(e) => setCustomExportWidth(e.target.value)}
                disabled={exporting}
              />
            </div>
            <div className="col-6">
              <label className="form-label small fw-semibold mb-1">Height (px)</label>
              <input
                type="number"
                className="form-control form-control-sm"
                min={MIN_CUSTOM_EXPORT_DIMENSION}
                max={MAX_CUSTOM_EXPORT_DIMENSION}
                value={customExportHeight}
                onChange={(e) => setCustomExportHeight(e.target.value)}
                disabled={exporting}
              />
            </div>
          </div>
        )}

        <div className="small text-muted mb-3">
          Export size: {computedExportDimensions.width} x {computedExportDimensions.height}px
        </div>

        {(() => {
          const compactRow = (exportSize === 'large' || exportSize === 'x-large') && exportLegendItems.length > 0;

          const titleBlock = (
            <div className="border rounded p-2 h-100">
              <div className="form-check form-switch mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`export-show-title-${cardId}`}
                  checked={exportShowTitle}
                  onChange={(e) => setExportShowTitle(e.target.checked)}
                  disabled={exporting}
                />
                <label className="form-check-label small fw-semibold" htmlFor={`export-show-title-${cardId}`}>
                  Show title
                </label>
              </div>
              {exportShowTitle && (
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={title || 'Chart title'}
                  value={exportTitleText}
                  onChange={(e) => setExportTitleText(e.target.value)}
                  disabled={exporting}
                />
              )}
            </div>
          );

          const legendBlock = exportLegendItems.length > 0 && (
            <div className="border rounded p-2 h-100">
              <div className="form-check form-switch mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`export-show-legend-${cardId}`}
                  checked={exportShowLegend}
                  onChange={(e) => setExportShowLegend(e.target.checked)}
                  disabled={exporting}
                />
                <label className="form-check-label small fw-semibold" htmlFor={`export-show-legend-${cardId}`}>
                  Show keys / legend
                </label>
              </div>
              {exportShowLegend && (
                <div className="d-flex flex-column gap-1">
                  {exportLegendItems.map((item, idx) => {
                    const hidden = !!exportItemHidden[idx];
                    const colorValue = exportItemColor[idx] || item.color;
                    return (
                      <div key={idx} className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary py-0 px-1"
                          style={{ fontSize: '0.7rem', flexShrink: 0 }}
                          title={hidden ? 'Show this key' : 'Hide this key'}
                          aria-label={hidden ? 'Show this key' : 'Hide this key'}
                          onClick={() => setExportItemHidden((prev) => ({ ...prev, [idx]: !prev[idx] }))}
                          disabled={exporting}
                        >
                          <i className={`bi ${hidden ? 'bi-eye-slash' : 'bi-eye'}`} />
                        </button>
                        <input
                          type="color"
                          className="p-0"
                          value={colorValue}
                          onChange={(e) => setExportItemColor((prev) => ({ ...prev, [idx]: e.target.value }))}
                          title="Change color"
                          disabled={exporting || hidden}
                          style={{ width: 28, height: 28, border: 'none', borderRadius: 4, cursor: 'pointer', flexShrink: 0, opacity: hidden ? 0.4 : 1 }}
                        />
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder={item.label}
                          value={exportLegendLabels[idx] || ''}
                          onChange={(e) => setExportLegendLabels((prev) => ({ ...prev, [idx]: e.target.value }))}
                          disabled={exporting || hidden}
                          style={{ opacity: hidden ? 0.5 : 1, textDecoration: hidden ? 'line-through' : 'none' }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );

          return compactRow ? (
            <div className="row g-2 mb-3">
              <div className="col-6">{titleBlock}</div>
              <div className="col-6">{legendBlock}</div>
            </div>
          ) : (
            <>
              <div className="mb-3">{titleBlock}</div>
              {legendBlock && <div className="mb-3">{legendBlock}</div>}
            </>
          );
        })()}

        <div className="mb-3">
          <label className="form-label small fw-semibold mb-1">File name</label>
          <div className="input-group input-group-sm">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={defaultFileNameBase}
              value={customFileName}
              onChange={(e) => setCustomFileName(e.target.value.replace(/\.png$/i, ''))}
              disabled={exporting}
            />
            <span className="input-group-text" style={{ fontSize: '0.75rem' }}>.png</span>
          </div>
          <div className="small text-muted mt-1">
            Default: {defaultFileNameBase}.png
          </div>
        </div>

        <div className="mb-3 border rounded p-2" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="small fw-semibold mb-2">Preview</div>
          <div
            style={{
              width: '100%',
              maxHeight: 420,
              backgroundColor: chartTheme.chartBg,
              border: `1px solid ${chartTheme.cardBorder}`,
              borderRadius: 4,
              overflow: 'auto',
            }}
          >
            {renderExportFrame(previewPatternPrefix)}
          </div>
          <div className="small text-muted mt-2">Preview uses the selected pixel size; scroll to inspect full frame when larger than the dialog.</div>

          {/* Hidden exact-size render target for export capture — identical layout to the preview above. */}
          <div
            style={{
              position: 'fixed',
              left: '-20000px',
              top: 0,
              width: computedExportDimensions.width,
              height: computedExportDimensions.height,
              pointerEvents: 'none',
              opacity: 0,
              overflow: 'hidden',
            }}
            aria-hidden="true"
          >
            {renderExportFrame(exportPatternPrefix, exportRenderRef)}
          </div>
        </div>
        </div>

        <div className="p-3 pt-2 border-top" style={{ flex: '0 0 auto' }}>
          {exportError && (
            <div className="alert alert-danger py-2 small mb-2" role="alert">{exportError}</div>
          )}

          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowExportModal(false)}
              disabled={exporting}
            >
              Cancel
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : 'Download PNG'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (fullscreen) {
    return (
      <div className="position-fixed top-0 start-0 w-100 h-100 p-3 d-flex flex-column"
        style={{ zIndex: 1060, backgroundColor: chartTheme.pageBg, color: chartTheme.pageText }}>
        {header}
        {body}
        {exportModal}
      </div>
    );
  }

  const card = (
    <Resizable
      axis="both"
      width={width}
      height={height}
      minConstraints={[MIN_CARD_WIDTH, MIN_CARD_HEIGHT]}
      maxConstraints={[MAX_CARD_WIDTH, MAX_CARD_HEIGHT]}
      resizeHandles={['s', 'e', 'se']}
      handle={(axis, ref) => renderResizeHandle(axis, ref, chartTheme)}
      onResize={handleResize}
    >
      <div className="border rounded p-3 position-relative d-flex flex-column"
        style={{ width, height, overflow: 'hidden', backgroundColor: chartTheme.cardBg, borderColor: chartTheme.cardBorder }}>
        {header}
        {body}
      </div>
    </Resizable>
  );

  // In fillWidth mode the card can grow past its container — wrap it so the
  // overflow scrolls horizontally instead of breaking the layout.
  return fillWidth
    ? <>
      <div ref={areaRef} style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', scrollbarGutter: 'stable both-edges' }}>{card}</div>
      {exportModal}
    </>
    : <>
      {card}
      {exportModal}
    </>;
}

// ---------- helpers ----------
function parseNum(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
}

// Multiply raw stored values to a common "ones" (actual) basis
const UNITS_MULTIPLIER = {
  billions: 1_000_000_000,
  billion: 1_000_000_000,
  millions: 1_000_000,
  million: 1_000_000,
  thousands: 1_000,
  thousand: 1_000,
  hundreds: 100,
  hundred: 100,
};

function unitsMultiplier(reportingUnits) {
  if (!reportingUnits) return 1;
  return UNITS_MULTIPLIER[reportingUnits.toLowerCase().trim()] ?? 1;
}

function evalComputed(row, cm) {
  const a = parseNum(row[cm.metricA?.value]);
  const b = parseNum(row[cm.metricB?.value]);
  if (a === null) return null;
  if (cm.op === '+') return a + (b ?? 0);
  if (cm.op === '-') return a - (b ?? 0);
  if (cm.op === '*') return b !== null ? a * b : null;
  if (cm.op === '/') return b && b !== 0 ? a / b : null;
  return a;
}

// ---------- time-series helpers ----------

// Flow / income-statement metrics are summed when rolling up periods.
// Balance-sheet, per-share, and ratio metrics are averaged (point-in-time).
const FLOW_METRICS = new Set([
  'revenue', 'total_revenue', 'net_revenue', 'gross_revenue',
  'cost_of_revenue', 'cost_of_goods_sold', 'cogs',
  'gross_profit',
  'operating_expenses', 'research_and_development', 'r_and_d',
  'sales_general_administrative', 'sg_and_a', 'sga',
  'operating_income', 'operating_income_loss',
  'net_income', 'net_earnings', 'net_income_loss',
  'ebitda', 'adjusted_ebitda',
  'free_cash_flow', 'capital_expenditures', 'capex',
  'depreciation', 'amortization', 'depreciation_and_amortization',
  'interest_expense', 'interest_income',
  'other_income', 'income_before_tax', 'income_tax_expense',
]);

function parseTimePeriod(row) {
  let year = row.fiscal_year ? Number(row.fiscal_year) : null;
  let quarter = row.fiscal_quarter ? Number(row.fiscal_quarter) : null;
  if (!year || !quarter) {
    const pd = row.period_end_date;
    if (pd) {
      const d = new Date(pd.includes('T') ? pd : pd + 'T00:00:00Z');
      if (!isNaN(d)) {
        if (!year) year = d.getUTCFullYear();
        if (!quarter) quarter = Math.ceil((d.getUTCMonth() + 1) / 3);
      }
    }
  }
  return { year, quarter };
}

function toSlotKey(year, quarter, granularity) {
  if (!year || !quarter) return null;
  if (granularity === 'half') return `${year}-H${quarter <= 2 ? 1 : 2}`;
  if (granularity === 'annual') return `${year}`;
  return `${year}-Q${quarter}`;
}

function slotLabel(key) {
  const qm = key.match(/^(\d{4})-Q(\d)$/);
  if (qm) return `Q${qm[2]}-${String(qm[1]).slice(2)}`;
  const hm = key.match(/^(\d{4})-H(\d)$/);
  if (hm) return `H${hm[2]}-${String(hm[1]).slice(2)}`;
  return key; // annual: "2021"
}

function generateTimeline(periods, granularity) {
  const valid = periods.filter(p => p.year && p.quarter);
  if (!valid.length) return [];
  const minY = Math.min(...valid.map(p => p.year));
  const maxY = Math.max(...valid.map(p => p.year));
  const slots = [];

  if (granularity === 'annual') {
    for (let y = minY; y <= maxY; y++) slots.push(`${y}`);
    return slots;
  }

  if (granularity === 'half') {
    const toH = p => p.quarter <= 2 ? 1 : 2;
    const minH = Math.min(...valid.filter(p => p.year === minY).map(toH));
    const maxH = Math.max(...valid.filter(p => p.year === maxY).map(toH));
    for (let y = minY; y <= maxY; y++) {
      const hStart = y === minY ? minH : 1;
      const hEnd = y === maxY ? maxH : 2;
      for (let h = hStart; h <= hEnd; h++) slots.push(`${y}-H${h}`);
    }
    return slots;
  }

  // quarterly — fill every slot between first and last observed quarter
  const minQ = Math.min(...valid.filter(p => p.year === minY).map(p => p.quarter));
  const maxQ = Math.max(...valid.filter(p => p.year === maxY).map(p => p.quarter));
  for (let y = minY; y <= maxY; y++) {
    const qStart = y === minY ? minQ : 1;
    const qEnd = y === maxY ? maxQ : 4;
    for (let q = qStart; q <= qEnd; q++) slots.push(`${y}-Q${q}`);
  }
  return slots;
}

function aggregateSlot(rowsInSlot, metricKey) {
  const vals = rowsInSlot
    .map(r => { const v = r[metricKey]; return (v !== null && v !== undefined) ? Number(v) : null; })
    .filter(v => v !== null && !isNaN(v));
  if (!vals.length) return null;
  // Division-derived metrics in computed series are always ratios — average them.
  // Known flow metrics are summed; everything else (balance-sheet, EPS, ratios) is averaged.
  if (FLOW_METRICS.has(metricKey.toLowerCase())) return vals.reduce((a, b) => a + b, 0);
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// react-select light styles
const SELECT_STYLES = {
  control: (p) => ({ ...p, minHeight: 34, fontSize: '0.82rem' }),
  option: (p) => ({ ...p, fontSize: '0.82rem' }),
  multiValue: (p) => ({ ...p, fontSize: '0.82rem' }),
  menuPortal: (p) => ({ ...p, zIndex: 9999 }),
};

// ---------- Tooltip ----------
function ChartTooltip({ active, payload, label, chartTheme }) {
  if (!active || !payload?.length) return null;
  const valid = payload.filter((p) => p.value != null);
  if (!valid.length) return null;
  return (
    <div style={{
      background: chartTheme.tooltipBg,
      border: `1px solid ${chartTheme.tooltipBorder}`,
      borderRadius: 6,
      padding: '8px 12px',
      fontSize: '0.8rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: chartTheme.tooltipText }}>{label}</div>
      {valid.map((p, i) => (
        <div key={i} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}: <strong>{typeof p.value === 'number' ? p.value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : p.value}</strong>
        </div>
      ))}
    </div>
  );
}

// ---------- per-metric config row ----------
function MetricRow({ metric, color, config, onChange, onRemove, chartTheme }) {
  return (
    <div className="d-flex align-items-center gap-2 mb-1 py-1 px-2 border rounded"
      style={{ fontSize: '0.8rem', backgroundColor: chartTheme.panelBg, borderColor: chartTheme.panelBorder, color: chartTheme.pageText }}>
      <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
      <span className="text-truncate" style={{ flex: 1, maxWidth: 200 }}>{metric.label}</span>
      <div className="btn-group btn-group-sm">
        {['bar', 'line'].map((t) => (
          <button key={t} className={`btn btn-sm ${config.type === t ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => onChange({ ...config, type: t })} style={{ fontSize: '0.72rem', padding: '1px 8px' }}>
            <i className={`bi ${t === 'bar' ? 'bi-bar-chart-fill' : 'bi-graph-up'}`} />
          </button>
        ))}
      </div>
      <div className="btn-group btn-group-sm">
        {['left', 'right'].map((ax) => (
          <button key={ax} className={`btn btn-sm ${config.yAxis === ax ? 'btn-secondary' : 'btn-outline-secondary'}`}
            onClick={() => onChange({ ...config, yAxis: ax })} style={{ fontSize: '0.72rem', padding: '1px 8px' }}>
            {ax === 'left' ? 'L' : 'R'}
          </button>
        ))}
      </div>
      <button className="btn btn-sm btn-outline-danger" onClick={onRemove} style={{ fontSize: '0.72rem', padding: '1px 6px' }}>
        <i className="bi bi-x" />
      </button>
    </div>
  );
}

// ---------- computed metric builder ----------
function ComputedRow({ cm, options, onChange, onRemove, color, chartTheme, selectStyles }) {
  const OPS = ['+', '-', '*', '/'];
  return (
    <div className="border rounded p-2 mb-2"
      style={{ fontSize: '0.8rem', backgroundColor: chartTheme.subtleBg, borderColor: chartTheme.panelBorder, color: chartTheme.pageText }}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
        <input className="form-control form-control-sm" placeholder="Label for this metric"
          value={cm.label} onChange={(e) => onChange({ ...cm, label: e.target.value })}
          style={{
            maxWidth: 200,
            fontSize: '0.8rem',
            backgroundColor: chartTheme.inputBg,
            borderColor: chartTheme.inputBorder,
            color: chartTheme.inputText,
          }} />
        <button className="btn btn-sm btn-outline-danger ms-auto" onClick={onRemove} style={{ fontSize: '0.72rem', padding: '1px 6px' }}>
          <i className="bi bi-x" />
        </button>
      </div>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <div style={{ flex: 1, minWidth: 140 }}>
          <Select options={options} value={cm.metricA} onChange={(v) => onChange({ ...cm, metricA: v })}
            placeholder="Metric A" styles={selectStyles} menuPortalTarget={document.body} />
        </div>
        <select className="form-select form-select-sm" value={cm.op}
          onChange={(e) => onChange({ ...cm, op: e.target.value })} style={{ width: 56, fontSize: '0.82rem' }}>
          {OPS.map((op) => <option key={op} value={op}>{op}</option>)}
        </select>
        <div style={{ flex: 1, minWidth: 140 }}>
          <Select options={options} value={cm.metricB} onChange={(v) => onChange({ ...cm, metricB: v })}
            placeholder="Metric B" styles={selectStyles} menuPortalTarget={document.body} />
        </div>
        <div className="btn-group btn-group-sm">
          {['bar', 'line'].map((t) => (
            <button key={t} className={`btn btn-sm ${cm.type === t ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => onChange({ ...cm, type: t })} style={{ fontSize: '0.72rem', padding: '1px 8px' }}>
              <i className={`bi ${t === 'bar' ? 'bi-bar-chart-fill' : 'bi-graph-up'}`} />
            </button>
          ))}
        </div>
        <div className="btn-group btn-group-sm">
          {['left', 'right'].map((ax) => (
            <button key={ax} className={`btn btn-sm ${cm.yAxis === ax ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => onChange({ ...cm, yAxis: ax })} style={{ fontSize: '0.72rem', padding: '1px 8px' }}>
              {ax === 'left' ? 'L' : 'R'}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Stable style references — prevents recharts v3 from dispatching on every render
const LEGEND_STYLE_SM = { fontSize: '0.75rem' };
const LEGEND_STYLE_MD = { fontSize: '0.78rem' };

// ---------- main component ----------
export default function ResearchChartsTab({ earnings, companyId, companyName }) {
  const [chartThemeKey, setChartThemeKey] = useState('hff');
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [metricConfigs, setMetricConfigs] = useState({});
  const [computedMetrics, setComputedMetrics] = useState([]);
  const [stacked, setStacked] = useState(false);
  const [granularity, setGranularity] = useState('quarter'); // 'quarter' | 'half' | 'annual'

  // Custom-chart view state (hide / fullscreen the chart card).
  const [customHidden, setCustomHidden] = useState(false);
  const [customFullscreen, setCustomFullscreen] = useState(false);

  // Predefined-card view state: hidden cards collapse to restore chips; one card
  // at a time can be fullscreened.
  const [hiddenPredefined, setHiddenPredefined] = useState(() => new Set());
  const [fullscreenPredefined, setFullscreenPredefined] = useState(null);
  const predefinedGridRef = useRef(null);
  const [predefinedDefaultWidth, setPredefinedDefaultWidth] = useState(DEFAULT_CARD_WIDTH);

  // Start predefined cards at half-row width for a 2x2 layout while preserving drag-resize.
  useLayoutEffect(() => {
    const el = predefinedGridRef.current;
    if (!el) return undefined;

    const sync = () => {
      const containerWidth = Math.floor(el.getBoundingClientRect().width || 0);
      if (!containerWidth) return;
      const target = Math.floor((containerWidth - PREDEFINED_GRID_GAP) / 2);
      const next = clamp(target, MIN_CARD_WIDTH, MAX_CARD_WIDTH);
      setPredefinedDefaultWidth((prev) => (
        Math.abs(prev - next) < RESIZE_SYNC_EPSILON_PX ? prev : next
      ));
    };

    let rafId = 0;
    const scheduleSync = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(sync);
    };

    sync();
    const ro = new ResizeObserver(scheduleSync);
    ro.observe(el);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, []);

  const hidePredefined = useCallback((title) => {
    setHiddenPredefined((prev) => new Set(prev).add(title));
    setFullscreenPredefined((cur) => (cur === title ? null : cur));
  }, []);
  const showPredefined = useCallback((title) => {
    setHiddenPredefined((prev) => {
      const next = new Set(prev);
      next.delete(title);
      return next;
    });
  }, []);
  const toggleFullscreenPredefined = useCallback((title) => {
    setFullscreenPredefined((cur) => (cur === title ? null : title));
  }, []);

  // Saved charts state
  const [savedCharts, setSavedCharts] = useState([]);
  const [activeChartId, setActiveChartId] = useState(null);   // currently loaded chart id
  const [activeChartName, setActiveChartName] = useState(''); // name of currently loaded chart
  const [showSaveRow, setShowSaveRow] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [chartsLoading, setChartsLoading] = useState(false);

  // Load saved charts whenever companyId changes
  const fetchSavedCharts = useCallback(async () => {
    if (!companyId) return;
    setChartsLoading(true);
    try {
      const charts = await researchService.listCharts(companyId);
      setSavedCharts(charts);
    } catch (_err) {
      // silently ignore load failure
    } finally {
      setChartsLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchSavedCharts(); }, [fetchSavedCharts]);

  // Apply a saved chart config to local state
  const applyChartConfig = useCallback((chart) => {
    const cfg = chart.config || {};
    setSelectedMetrics(cfg.selectedMetrics || []);
    setMetricConfigs(cfg.metricConfigs || {});
    setComputedMetrics(cfg.computedMetrics || []);
    setGranularity(cfg.granularity || 'quarter');
    setStacked(cfg.stacked || false);
    setActiveChartId(chart.chart_id);
    setActiveChartName(chart.name);
    setShowSaveRow(false);
  }, []);

  // Build the config snapshot from current state
  const buildConfig = () => ({
    selectedMetrics,
    metricConfigs,
    computedMetrics,
    granularity,
    stacked,
  });

  const handleSaveNew = async () => {
    if (!companyId || !saveName.trim()) return;
    setSaving(true);
    try {
      const created = await researchService.createChart(companyId, saveName.trim(), buildConfig());
      setSavedCharts((prev) => [created, ...prev]);
      setActiveChartId(created.chart_id);
      setActiveChartName(created.name);
      setShowSaveRow(false);
      setSaveName('');
    } catch (_err) {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!companyId || !activeChartId || !saveName.trim()) return;
    setSaving(true);
    try {
      const updated = await researchService.updateChart(companyId, activeChartId, saveName.trim(), buildConfig());
      setSavedCharts((prev) => prev.map((c) => (c.chart_id === activeChartId ? updated : c)));
      setActiveChartName(updated.name);
      setShowSaveRow(false);
    } catch (_err) {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChart = async (chartId) => {
    if (!companyId) return;
    try {
      await researchService.deleteChart(companyId, chartId);
      setSavedCharts((prev) => prev.filter((c) => c.chart_id !== chartId));
      if (activeChartId === chartId) {
        setActiveChartId(null);
        setActiveChartName('');
      }
    } catch (_err) {
      // ignore
    }
  };

  const savedChartOptions = savedCharts.map((c) => ({
    value: c.chart_id,
    label: c.name,
    chart: c,
  }));

  const allMetricKeys = useMemo(() => {
    const keys = new Set();
    (earnings || []).forEach((r) => {
      Object.entries(r.metrics || {}).forEach(([k, v]) => {
        if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
          // Nested object (e.g. custom_metrics) — expose sub-keys directly
          Object.keys(v).forEach((sk) => keys.add(sk));
        } else {
          keys.add(k);
        }
      });
    });
    return Array.from(keys).sort();
  }, [earnings]);

  const metricOptions = useMemo(
    () => allMetricKeys.map((k) => ({ value: k, label: k })),
    [allMetricKeys]
  );

  // Base chart data: normalized to actual units, sorted into a full time-series
  // with every slot between first and last period filled (null = no data that period).
  const chartData = useMemo(() => {
    const rows = earnings || [];
    if (!rows.length) return [];

    // Normalize stored values to actual (ones) basis; flatten nested objects
    const normalized = rows.map(r => {
      const mult = unitsMultiplier(r.reporting_units);
      const metrics = {};
      Object.entries(r.metrics || {}).forEach(([k, v]) => {
        if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
          // Nested object (e.g. custom_metrics): flatten sub-keys directly
          Object.entries(v).forEach(([sk, sv]) => {
            const n = parseNum(sv);
            metrics[sk] = n !== null ? n * mult : null;
          });
        } else {
          const n = parseNum(v);
          metrics[k] = n !== null ? n * mult : null;
        }
      });
      return { ...parseTimePeriod(r), metrics };
    });

    const timeline = generateTimeline(normalized, granularity);

    // Group rows by time slot
    const slotMap = {};
    normalized.forEach(row => {
      const key = toSlotKey(row.year, row.quarter, granularity);
      if (!key) return;
      (slotMap[key] = slotMap[key] || []).push(row.metrics);
    });

    const allKeys = [...new Set(normalized.flatMap(r => Object.keys(r.metrics)))];

    return timeline.map(slot => {
      const rowsInSlot = slotMap[slot] || [];
      const point = { label: slotLabel(slot), _slot: slot };
      allKeys.forEach(k => { point[k] = aggregateSlot(rowsInSlot, k); });
      return point;
    });
  }, [earnings, granularity]);

  // Inject computed metric columns
  const finalChartData = useMemo(() => {
    if (!computedMetrics.length) return chartData;
    return chartData.map((row) => {
      const r = { ...row };
      computedMetrics.forEach((cm) => {
        if (cm.label && cm.metricA) r[`__cm_${cm.id}`] = evalComputed(r, cm);
      });
      return r;
    });
  }, [chartData, computedMetrics]);

  const handleMetricChange = useCallback((selected) => {
    setSelectedMetrics(selected || []);
    setMetricConfigs((prev) => {
      const next = { ...prev };
      (selected || []).forEach((s) => { if (!next[s.value]) next[s.value] = { type: 'bar', yAxis: 'left' }; });
      return next;
    });
  }, []);

  const addComputed = useCallback(() => {
    setComputedMetrics((prev) => [...prev, { id: Date.now(), label: '', metricA: null, op: '+', metricB: null, type: 'line', yAxis: 'right' }]);
  }, []);

  const chartTheme = CHART_THEMES[chartThemeKey] || CHART_THEMES.hff;
  const isDarkTheme = chartThemeKey === 'rebellionaire';
  const selectStyles = useMemo(() => {
    if (!isDarkTheme) return SELECT_STYLES;
    return {
      ...SELECT_STYLES,
      control: (p, s) => ({
        ...SELECT_STYLES.control(p, s),
        backgroundColor: chartTheme.inputBg,
        borderColor: s.isFocused ? chartTheme.series[0] : chartTheme.inputBorder,
        boxShadow: 'none',
      }),
      input: (p) => ({ ...p, color: chartTheme.inputText }),
      singleValue: (p) => ({ ...p, color: chartTheme.inputText }),
      multiValue: (p, s) => ({
        ...SELECT_STYLES.multiValue(p, s),
        backgroundColor: chartTheme.subtleBg,
        color: chartTheme.inputText,
      }),
      multiValueLabel: (p) => ({ ...p, color: chartTheme.inputText }),
      placeholder: (p) => ({ ...p, color: chartTheme.inputPlaceholder }),
      menu: (p) => ({ ...p, backgroundColor: chartTheme.panelBg, border: `1px solid ${chartTheme.panelBorder}` }),
      option: (p, s) => ({
        ...SELECT_STYLES.option(p, s),
        backgroundColor: s.isFocused ? chartTheme.subtleBg : chartTheme.panelBg,
        color: chartTheme.inputText,
      }),
    };
  }, [isDarkTheme, chartTheme]);

  // All series in draw order
  const allSeries = useMemo(() => [
    ...selectedMetrics.map((m, i) => ({
      key: m.value,
      label: m.label,
      cfg: metricConfigs[m.value] || { type: 'bar', yAxis: 'left' },
      color: chartTheme.series[i % chartTheme.series.length],
      striped: i >= 5,
      stripeIndex: i % 5,
    })),
    ...computedMetrics.filter((cm) => cm.label && cm.metricA).map((cm, i) => ({
      key: `__cm_${cm.id}`,
      label: cm.label,
      cfg: { type: cm.type, yAxis: cm.yAxis },
      color: chartTheme.series[(selectedMetrics.length + i) % chartTheme.series.length],
      striped: (selectedMetrics.length + i) >= 5,
      stripeIndex: (selectedMetrics.length + i) % 5,
    })),
  ], [selectedMetrics, metricConfigs, computedMetrics, chartTheme.series, chartThemeKey]);

  const hasRightAxis = allSeries.some((s) => s.cfg.yAxis === 'right');
  const hasBars = allSeries.some((s) => s.cfg.type === 'bar');

  // Predefined charts
  const PREDEFINED = [
    { title: 'Revenue', keys: ['revenue', 'total_revenue', 'net_revenue'] },
    { title: 'Net Income', keys: ['net_income', 'net_earnings'] },
    { title: 'EPS', keys: ['eps', 'eps_diluted', 'eps_basic'] },
    { title: 'Operating Income', keys: ['operating_income', 'operating_income_loss'] },
  ];
  const visiblePredefined = PREDEFINED.filter(({ keys }) => keys.some((k) => allMetricKeys.includes(k)));
  const hiddenVisiblePredefined = visiblePredefined.filter(({ title }) => hiddenPredefined.has(title));

  if (!earnings || earnings.length === 0) {
    return (
      <div className="p-3" style={{ backgroundColor: chartTheme.pageBg, color: chartTheme.textMuted }}>
        No structured earnings data yet. Upload an Earnings file to see charts.
      </div>
    );
  }

  const fmtTick = (v) => (typeof v === 'number' ? v.toLocaleString() : v);

  return (
    <div className="p-3" style={{ backgroundColor: chartTheme.pageBg, color: chartTheme.pageText }}>
      {/* Time granularity selector — affects all charts */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <span className="small fw-semibold" style={{ color: chartTheme.textMuted }}>Group by:</span>
        <div className="btn-group btn-group-sm">
          {[{ value: 'quarter', label: 'Quarter' }, { value: 'half', label: 'Semi-Annual' }, { value: 'annual', label: 'Annual' }].map(o => (
            <button key={o.value}
              className={`btn btn-sm ${granularity === o.value ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setGranularity(o.value)}
              style={{ fontSize: '0.75rem' }}>
              {o.label}
            </button>
          ))}
        </div>
        <span className="small ms-2" style={{ color: chartTheme.textMuted }}>
          {granularity === 'quarter' && 'Flow metrics summed per quarter. Balance-sheet & ratios averaged.'}
          {granularity === 'half' && 'Flow metrics summed per half-year. Balance-sheet & ratios averaged.'}
          {granularity === 'annual' && 'Flow metrics summed per year. Balance-sheet & ratios averaged.'}
        </span>
        <div className="ms-auto d-flex align-items-center gap-2">
          <span className="small fw-semibold" style={{ color: chartTheme.textMuted }}>Chart Theme:</span>
          <div className="btn-group btn-group-sm" role="group" aria-label="Chart theme toggle">
            {Object.entries(CHART_THEMES).map(([key, cfg]) => (
              <button
                key={key}
                className={`btn btn-sm ${chartThemeKey === key ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => setChartThemeKey(key)}
                style={{ fontSize: '0.75rem' }}
              >
                {cfg.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {visiblePredefined.length > 0 && (
        <div className="mb-4">
          {/* Restore chips for hidden cards */}
          {hiddenVisiblePredefined.length > 0 && (
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <span className="small" style={{ color: chartTheme.textMuted }}>Hidden:</span>
              {hiddenVisiblePredefined.map(({ title }) => (
                  <button key={title} className="btn btn-sm btn-outline-secondary"
                    style={{ fontSize: '0.75rem' }} title="Show chart"
                    onClick={() => showPredefined(title)}>
                    <i className="bi bi-eye me-1" />{title}
                  </button>
                ))}
            </div>
          )}

          {/* Predefined charts start in a full 2x2 grid (on desktop) */}
          <div ref={predefinedGridRef} className="d-flex flex-wrap gap-3 align-items-start">
            {visiblePredefined
              .filter(({ title }) => !hiddenPredefined.has(title))
              .map(({ title, keys }) => {
                const resolved = keys.filter((k) => allMetricKeys.includes(k));
                return (

                  <ResizableCard
                    key={title}
                    title={title}
                    chartTheme={chartTheme}
                    companyName={companyName}
                    exportLegendItems={resolved.map((k, i) => ({
                      label: k,
                      color: chartTheme.series[i % 5],
                    }))}
                    defaultWidth={predefinedDefaultWidth}
                    defaultHeight={300}
                    fullscreen={fullscreenPredefined === title}
                    onHide={() => hidePredefined(title)}
                    onToggleFullscreen={() => toggleFullscreenPredefined(title)}
                    renderChart={(patternPrefix, opts = {}) => (
                      <ComposedChart
                        data={finalChartData}
                        margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
                        style={{ backgroundColor: chartTheme.chartBg, borderRadius: 4 }}
                      >
                        {renderStripeDefs(patternPrefix, chartTheme)}
                        <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridStroke} />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: chartTheme.textMuted }} stroke={chartTheme.axisStroke} />
                        <YAxis yAxisId="left" tick={{ fontSize: 10, fill: chartTheme.textMuted }} width={55} tickFormatter={fmtTick} stroke={chartTheme.axisStroke} />
                        <Tooltip content={(props) => <ChartTooltip {...props} chartTheme={chartTheme} />} />
                        {opts.showLegend !== false && <Legend wrapperStyle={LEGEND_STYLE_SM} />}
                        {finalChartData.some((row) => resolved.some((k) => row[k] < 0)) && (
                          <ReferenceLine yAxisId="left" y={0} stroke={chartTheme.zeroLine} strokeWidth={1.5} />
                        )}
                        {resolved.map((k, i) => {
                          const override = opts.seriesOverrides?.[i];
                          if (override?.hidden) return null;
                          return (
                            <Bar
                              key={k}
                              yAxisId="left"
                              dataKey={k}
                              fill={override?.color || (i >= 5
                                ? `url(#${patternPrefix}-stripe-${i % 5})`
                                : chartTheme.series[i % chartTheme.series.length])}
                              isAnimationActive={false}
                              maxBarSize={24} radius={[3, 3, 0, 0]} />
                          );
                        })}
                      </ComposedChart>
                    )}
                  />
                );
              })}
          </div>
        </div>
      )}

      {/* Custom chart builder */}
      <div className="border rounded p-3" style={{ backgroundColor: chartTheme.panelBg, borderColor: chartTheme.panelBorder }}>
        {/* Header */}
        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          <span className="fw-bold">Custom Chart</span>
          <div className="ms-auto d-flex align-items-center gap-2 flex-wrap">
            {hasBars && (
              <div className="form-check form-switch mb-0 d-flex align-items-center gap-1">
                <input className="form-check-input" type="checkbox" id="stack-toggle"
                  checked={stacked} onChange={(e) => setStacked(e.target.checked)} />
                <label className="form-check-label small" htmlFor="stack-toggle">Stack bars</label>
              </div>
            )}
            <button className="btn btn-sm btn-outline-secondary" onClick={() => {
              setSelectedMetrics([]); setMetricConfigs({}); setComputedMetrics([]);
              setActiveChartId(null); setActiveChartName('');
            }}>
              Clear
            </button>
          </div>
        </div>

        {/* Saved charts row */}
        {companyId && (
          <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
            {/* Load dropdown */}
            <div style={{ minWidth: 200, maxWidth: 300 }}>
              <Select
                options={savedChartOptions}
                value={savedChartOptions.find((o) => o.value === activeChartId) || null}
                onChange={(opt) => opt && applyChartConfig(opt.chart)}
                placeholder={chartsLoading ? 'Loading…' : savedCharts.length === 0 ? 'No saved charts' : 'Load a saved chart…'}
                isDisabled={chartsLoading || savedCharts.length === 0}
                isClearable={false}
                styles={SELECT_STYLES}
                menuPortalTarget={document.body}
              />
            </div>

            {/* Save / update button */}
            <button
              className={`btn btn-sm ${showSaveRow ? 'btn-primary' : 'btn-outline-primary'}`}
              title={activeChartId ? 'Save or update this chart' : 'Save current chart'}
              onClick={() => { setSaveName(activeChartName); setShowSaveRow((s) => !s); }}
            >
              <i className="bi bi-floppy me-1" />
              {activeChartId ? 'Save' : 'Save Chart'}
            </button>

            {/* Delete loaded chart */}
            {activeChartId && !showSaveRow && (
              <button
                className="btn btn-sm btn-outline-danger"
                title="Delete this saved chart"
                onClick={() => handleDeleteChart(activeChartId)}
              >
                <i className="bi bi-trash me-1" />
                Delete
              </button>
            )}

            {/* Loaded chart name badge */}
            {activeChartId && !showSaveRow && (
              <span className="small fst-italic" style={{ color: chartTheme.textMuted }}>
                <i className="bi bi-bookmark-check me-1" />
                {activeChartName}
              </span>
            )}

            {/* Inline save / update input row */}
            {showSaveRow && (
              <div className="d-flex align-items-center gap-2">
                <input
                  className="form-control form-control-sm"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="Chart name…"
                  autoFocus
                  style={{ width: 180, fontSize: '0.8rem' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { activeChartId ? handleUpdate() : handleSaveNew(); }
                    if (e.key === 'Escape') setShowSaveRow(false);
                  }}
                />
                {activeChartId && (
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={saving || !saveName.trim()}
                    onClick={handleUpdate}
                  >
                    <i className="bi bi-arrow-repeat me-1" />
                    {saving ? 'Saving…' : 'Update'}
                  </button>
                )}
                <button
                  className="btn btn-sm btn-outline-primary"
                  disabled={saving || !saveName.trim()}
                  onClick={handleSaveNew}
                >
                  <i className="bi bi-floppy me-1" />
                  {saving ? 'Saving…' : activeChartId ? 'Save as New' : 'Save'}
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowSaveRow(false)}>
                  <i className="bi bi-x me-1" />
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Metric select */}
        <div className="mb-3">
          <label className="form-label small fw-semibold mb-1">Add Metrics</label>
          <Select isMulti options={metricOptions} value={selectedMetrics} onChange={handleMetricChange}
            placeholder="Search and select metrics…" styles={selectStyles}
            menuPortalTarget={document.body} closeMenuOnSelect={false} />
        </div>

        {/* Per-metric config rows */}
        {selectedMetrics.length > 0 && (
          <div className="mb-3">
            <div className="small fw-semibold mb-1" style={{ color: chartTheme.textMuted }}>Series — toggle chart type &amp; axis</div>
            {selectedMetrics.map((m, i) => (
              <MetricRow key={m.value} metric={m} color={chartTheme.series[i % chartTheme.series.length]}
                chartTheme={chartTheme}
                config={metricConfigs[m.value] || { type: 'bar', yAxis: 'left' }}
                onChange={(cfg) => setMetricConfigs((p) => ({ ...p, [m.value]: cfg }))}
                onRemove={() => setSelectedMetrics((p) => p.filter((x) => x.value !== m.value))} />
            ))}
          </div>
        )}

        {/* Computed metrics */}
        {computedMetrics.length > 0 && (
          <div className="mb-3">
            <div className="small fw-semibold mb-1" style={{ color: chartTheme.textMuted }}>Computed Metrics</div>
            {computedMetrics.map((cm, i) => (
              <ComputedRow key={cm.id} cm={cm} options={metricOptions}
                color={chartTheme.series[(selectedMetrics.length + i) % chartTheme.series.length]}
                chartTheme={chartTheme}
                selectStyles={selectStyles}
                onChange={(updates) => setComputedMetrics((p) => p.map((x) => x.id === cm.id ? { ...x, ...updates } : x))}
                onRemove={() => setComputedMetrics((p) => p.filter((x) => x.id !== cm.id))} />
            ))}
          </div>
        )}

        <div className="mb-3">
          <button className="btn btn-sm btn-outline-primary" onClick={addComputed}>
            <i className="bi bi-plus-lg me-1" />Add Computed Metric (A ÷ B, A − B…)
          </button>
        </div>

        {/* Chart */}
        {allSeries.length > 0 ? (
          customHidden ? (
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setCustomHidden(false)}>
              <i className="bi bi-eye me-1" />Show chart
            </button>
          ) : (
            <ResizableCard
              title=""
              chartTheme={chartTheme}
              companyName={companyName}
              exportLegendItems={allSeries.map((s) => ({ label: s.label, color: s.color }))}
              fillWidth
              defaultHeight={320}
              fullscreen={customFullscreen}
              onHide={() => setCustomHidden(true)}
              onToggleFullscreen={() => setCustomFullscreen((f) => !f)}
              renderChart={(patternPrefix, opts = {}) => (
                <ComposedChart
                  data={finalChartData}
                  margin={{ top: 4, right: hasRightAxis ? 64 : 16, left: 8, bottom: 4 }}
                  style={{ backgroundColor: chartTheme.chartBg, borderRadius: 4 }}
                >
                  {renderStripeDefs(patternPrefix, chartTheme)}
                  <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridStroke} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: chartTheme.textMuted }} stroke={chartTheme.axisStroke} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: chartTheme.textMuted }} width={64} tickFormatter={fmtTick} stroke={chartTheme.axisStroke} />
                  {hasRightAxis && (
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: chartTheme.textMuted }} width={64} tickFormatter={fmtTick} stroke={chartTheme.axisStroke} />
                  )}
                  <Tooltip content={(props) => <ChartTooltip {...props} chartTheme={chartTheme} />} />
                  {opts.showLegend !== false && <Legend wrapperStyle={LEGEND_STYLE_MD} />}
                  {finalChartData.some((row) => allSeries.some((s) => row[s.key] < 0)) && (
                    <ReferenceLine yAxisId="left" y={0} stroke={chartTheme.zeroLine} strokeWidth={1.5} />
                  )}
                  {allSeries.map((s, i) => {
                    const override = opts.seriesOverrides?.[i];
                    if (override?.hidden) return null;
                    return s.cfg.type === 'bar' ? (
                      <Bar key={s.key} yAxisId={s.cfg.yAxis} dataKey={s.key} name={s.label}
                        fill={override?.color || (s.striped ? `url(#${patternPrefix}-stripe-${s.stripeIndex})` : s.color)}
                        isAnimationActive={false}
                        maxBarSize={32} radius={[3, 3, 0, 0]}
                        stackId={stacked ? 'stack' : undefined} />
                    ) : (
                      <Line key={s.key} yAxisId={s.cfg.yAxis} type="monotone" dataKey={s.key}
                        name={s.label} stroke={override?.color || s.color} dot={{ r: 3 }} strokeWidth={2} connectNulls
                        isAnimationActive={false} />
                    );
                  })}
                </ComposedChart>
              )}
            />
          )
        ) : (
          <div className="small py-4 text-center border rounded"
            style={{ color: chartTheme.textMuted, backgroundColor: chartTheme.subtleBg, borderColor: chartTheme.panelBorder }}>
            Select metrics above to build a chart.
          </div>
        )}
      </div>
    </div>
  );
}
